import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mui/material';

import {
  Page,
  useAppLayoutContextConsumer,
  Button,
  StatusChip,
  ArchiveIcon,
  EditIcon,
  VisibilityOutlinedIcon,
  UnarchiveIcon,
} from '@occc-app/design-system';
import { SteeringWalletStatus } from '@occc/graphql';

import { WalletDetails } from './WalletDetails';
import { getBreadcrumbLinks } from './utils';

import { routeCreator } from '../utils';
import { WalletData } from '../types';
import { chipVariantByStatus, titleByStatus } from '../constants';

type WalletDetailsPageProps = {
  walletData: WalletData;
  onActivate: () => void;
  onDeactivate: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  isUserPermittedToEditWallet: boolean;
  isUserPermittedToArchiveWallet: boolean;
  isUserPermittedToViewWalletConfiguration: boolean;
};

export const WalletDetailsPage: React.FC<WalletDetailsPageProps> = ({
  walletData,
  onActivate,
  onDeactivate,
  onArchive,
  onUnarchive,
  isUserPermittedToEditWallet,
  isUserPermittedToArchiveWallet,
  isUserPermittedToViewWalletConfiguration,
}) => {
  const wallet = walletData.wallet.data;
  const walletName = wallet?.name;

  const links = React.useMemo(() => getBreadcrumbLinks(walletName), [walletName]);
  useAppLayoutContextConsumer(links);

  const actions = useMemo(() => {
    if (!wallet) {
      return [];
    }

    const isViewConfigurationButtonVisible =
      !isUserPermittedToEditWallet && isUserPermittedToViewWalletConfiguration;

    if (isViewConfigurationButtonVisible) {
      return (
        <Button
          key='viewConfiguration'
          color='secondary'
          variant='contained'
          component={Link}
          to={routeCreator.update(wallet.uuid)}
          startIcon={<VisibilityOutlinedIcon />}
          role='button'
        >
          View Configuration
        </Button>
      );
    }

    const editButton = isUserPermittedToEditWallet && (
      <Button
        key='edit'
        color='secondary'
        variant='contained'
        component={Link}
        to={routeCreator.update(wallet.uuid)}
        startIcon={<EditIcon />}
        role='button'
      >
        Edit
      </Button>
    );

    const activateButton = (
      <Button key='activate' variant='contained' color='secondary' onClick={onActivate}>
        Activate
      </Button>
    );

    const deactivateButton = (
      <Button key='deactivate' variant='contained' color='secondary' onClick={onDeactivate}>
        Deactivate
      </Button>
    );

    const archiveButton = isUserPermittedToArchiveWallet && (
      <Button
        key='archive'
        startIcon={<ArchiveIcon />}
        variant='contained'
        onClick={onArchive}
        color='secondary'
      >
        Archive
      </Button>
    );

    const unarchiveButton = (
      <Button
        key='unarchive'
        startIcon={<UnarchiveIcon />}
        variant='contained'
        onClick={onUnarchive}
        color='secondary'
      >
        Unarchive
      </Button>
    );

    const actionItems: React.ReactNode[] = [];
    if (wallet.status === SteeringWalletStatus.Active) {
      actionItems.push(deactivateButton, archiveButton, editButton);
    } else if (wallet.status === SteeringWalletStatus.Draft) {
      actionItems.push(activateButton, archiveButton, editButton);
    } else if (wallet.status === SteeringWalletStatus.Archived) {
      actionItems.push(unarchiveButton);
    }

    return actionItems;
  }, [
    wallet,
    isUserPermittedToEditWallet,
    isUserPermittedToViewWalletConfiguration,
    onActivate,
    onDeactivate,
    isUserPermittedToArchiveWallet,
    onArchive,
    onUnarchive,
  ]);

  const pageSubtitle = wallet && (
    <Box mt={1}>
      <StatusChip
        label={titleByStatus[wallet.status]}
        variant={chipVariantByStatus[wallet.status]}
      />
    </Box>
  );

  return (
    <Page
      browserTabTitle='Wallet'
      actions={actions}
      pageHeaderProps={{
        title: 'Wallet Details',
        subtitle: pageSubtitle,
      }}
      containerProps={{ variant: 'md' }}
    >
      <WalletDetails walletData={walletData} />
    </Page>
  );
};
